import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material'
import { Email as EmailIcon } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  AssignmentIcon,
  ClockIcon,
  CorrectIcon,
  CourseIcon,
  IncorrectIcon,
} from 'src/v3/core/icons'
import {
  CourseWithStats,
  fetchAssignmentDetailV4,
  fetchAllAssignmentUserProgressV4,
  fetchLinkedServices,
  selectActiveAssignmentDetailV4,
  selectActiveAssignmentDetailV4Status,
  selectAssignmentUserProgressV4,
  selectAssignmentUserProgressV4Status,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { SelectedDataForCredit } from './types'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import BulkCourseCreditModal from './components/modals/bulk-course-credit-modal'
import { giveCourseCredit } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import { getProgressColor, getIsOverdue } from './utils/helpers'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { ASSIGNMENTS_URLS } from '../constants'

dayjs.extend(utc)

function AssignmentProgressSkeleton(): ReactElement {
  return (
    <Card sx={{ bgcolor: 'background.paper' }} className="p-6">
      <Paper
        className="p-4 mb-6"
        sx={{ bgcolor: 'background.paperDark', borderRadius: 2 }}
      >
        <Box className="flex items-start justify-between gap-4 mb-3">
          <Box className="flex-1">
            <Box className="flex items-center gap-1 mb-2">
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width="60%" height={28} />
            </Box>
            <Box className="flex items-center gap-1 mb-2">
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width="50%" height={20} />
            </Box>
            <Box className="flex items-center gap-1 mb-1">
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width="80%" height={20} />
            </Box>
            <Box className="flex items-center gap-1">
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
          </Box>
          <Box className="flex items-center gap-2">
            <Skeleton
              variant="rectangular"
              width={100}
              height={32}
              sx={{ borderRadius: 4 }}
            />
            <Skeleton
              variant="rectangular"
              width={80}
              height={32}
              sx={{ borderRadius: 4 }}
            />
          </Box>
        </Box>
        <Box>
          <Box className="flex items-center justify-between mb-1">
            <Skeleton variant="text" width={120} height={20} />
            <Skeleton variant="text" width={80} height={16} />
          </Box>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={8}
            sx={{ borderRadius: 4 }}
          />
        </Box>
      </Paper>

      <Box className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Paper className="p-4" sx={{ bgcolor: 'background.paperDark' }}>
          <Skeleton variant="text" width={200} height={32} className="mb-4" />
          <Box sx={{ maxHeight: 400 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Box key={index} className="flex items-center gap-2 py-1 mb-1">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width="70%" height={16} />
              </Box>
            ))}
          </Box>
        </Paper>
        <Paper className="p-4" sx={{ bgcolor: 'background.paperDark' }}>
          <Skeleton variant="text" width={200} height={32} className="mb-4" />
          <Box sx={{ maxHeight: 400 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Box key={index} className="flex items-center gap-2 py-1 mb-1">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width="60%" height={16} />
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Card>
  )
}

function formatStatus(status: string): string {
  switch (status) {
    case 'Completed':
      return 'Completed'
    case 'InProgress':
      return 'In Progress'
    case 'NotStarted':
      return 'Not Started'
    default:
      return status
  }
}

export default function AssignmentProgress(): ReactElement {
  useScrollToTop()

  let { assignmentID, userID } = useParams<{
    assignmentID: string
    userID: string
  }>()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  let [courseCreditModalOpen, setCourseCreditModalOpen] = useState(false)
  let [selectedDataForCredit, setSelectedDataForCredit] =
    useState<SelectedDataForCredit | null>(null)

  let assignment = useAppSelector(selectActiveAssignmentDetailV4)
  let assignmentDetailStatus = useAppSelector(
    selectActiveAssignmentDetailV4Status,
  )
  let assignmentUserProgressV4Status = useAppSelector(
    selectAssignmentUserProgressV4Status,
  )
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let userProgress = useAppSelector(selectAssignmentUserProgressV4)

  let selectedUser = userProgress.find((u) => u.userID === userID)
  let completedCourses =
    selectedUser?.courseProgress?.filter((cp) => cp.status === 'Completed') ||
    []
  let completedCourseIDs = new Set(
    selectedUser?.courseProgress
      ?.filter((cp) => cp.status === 'Completed')
      ?.map((cp) => cp.courseID) || [],
  )
  let outstandingCourses =
    assignment?.courses?.filter(
      (course: CourseWithStats) => !completedCourseIDs.has(course.id),
    ) || []

  let getCourseNameForCredit = (course: CourseWithStats, index: number) =>
    course.name && course.displayName
      ? `${course.name} - ${course.displayName}`
      : course.name || course.displayName || `Course ${index + 1}`

  let linkedServiceName = useLinkedServiceName(assignment?.linkedServiceID)

  useEffect(() => {
    if (
      assignmentID &&
      (assignmentDetailStatus === 'uninitialized' ||
        assignment?.id !== assignmentID)
    ) {
      dispatch(fetchAssignmentDetailV4(assignmentID))
    }
  }, [assignmentID, assignmentDetailStatus, assignment?.id, dispatch])

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    dispatch(
      fetchAllAssignmentUserProgressV4({
        assignmentId: assignmentID!,
        includeDetails: true,
      }),
    )
  }, [assignmentID, dispatch])

  let handleGiveCourseCredit = async () => {
    if (
      !assignmentID ||
      !selectedDataForCredit?.courseId ||
      !selectedDataForCredit?.userId
    )
      return

    setIsSubmitting(true)

    let payload = {
      assignmentID,
      userIDs: [selectedDataForCredit.userId],
      courseIDs: [selectedDataForCredit.courseId],
    }

    await giveCourseCredit(payload)
      .then(() =>
        Promise.all([
          dispatch(
            fetchAllAssignmentUserProgressV4({
              assignmentId: assignmentID!,
              includeDetails: true,
            }),
          ),
          dispatch(fetchAssignmentDetailV4(assignmentID)),
        ]),
      )
      .then(() =>
        showSuccess(
          `Successfully gave ${selectedDataForCredit.userName} course credit for ${selectedDataForCredit.courseName}.`,
        ),
      )
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(
          `Error giving ${selectedDataForCredit.userName} course credit for ${selectedDataForCredit.courseName}.`,
          'Payload: ',
          payload,
          'Error: ',
          err,
        )
        showError(
          `Error giving ${selectedDataForCredit.userName} course credit for ${selectedDataForCredit.courseName}.`,
        )
      })
      .finally(() => {
        setCourseCreditModalOpen(false)
        setSelectedDataForCredit(null)
        setIsSubmitting(false)
      })
  }

  let isOverdue = getIsOverdue(assignment?.dueDate || '')
  let isLoading =
    assignmentDetailStatus === 'pending' ||
    assignmentUserProgressV4Status === 'pending' ||
    !selectedUser

  let progressPercentage = selectedUser?.progressPercentage || 0
  let progressColor = getProgressColor(progressPercentage, isOverdue)

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="mb-4">
        <PageTitleWithLinkedService
          pageTitle="User Assignment Progress"
          linkedServiceName={linkedServiceName}
          moduleName="Assignments"
          handleBackClick={() =>
            navigate(`${ASSIGNMENTS_URLS.INDEX}/${assignmentID}`)
          }
          isLoading={isLoading}
        />
      </Box>
      {isLoading ? (
        <Box aria-live="polite" aria-busy="true">
          <AssignmentProgressSkeleton />
        </Box>
      ) : (
        <Card
          sx={{ bgcolor: 'background.paper' }}
          className="p-6"
          role="main"
          aria-live="polite"
        >
          <Box className="flex items-start justify-between gap-4 mb-3">
            <Box className="flex-1">
              <Box className="flex items-center gap-2 mb-2">
                <AssignmentIcon
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                  aria-hidden="true"
                />
                <Typography
                  variant="subtitle2"
                  component="h3"
                  color="text.secondary"
                >
                  {assignment?.name || 'Unknown Assignment'}
                </Typography>
              </Box>
              <Typography variant="h6" component="h2" className="mb-6">
                {selectedUser?.userName || 'Unknown User'}
              </Typography>
              <Box className="flex items-center gap-2 mb-2">
                <EmailIcon
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                  aria-hidden="true"
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedUser?.email || 'No email provided'}
                </Typography>
              </Box>
              <Box className="flex items-center gap-2 mb-2">
                <ClockIcon
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                  aria-hidden="true"
                />
                <Typography variant="caption" color="text.secondary">
                  Last Activity:{' '}
                  {selectedUser?.lastActivityDate
                    ? dayjs(selectedUser.lastActivityDate).fromNow()
                    : 'Never'}
                </Typography>
              </Box>
            </Box>
            <Box
              className="flex items-center gap-2"
              role="status"
              aria-live="polite"
            >
              <Chip
                label={formatStatus(selectedUser?.status || 'NotStarted')}
                color={
                  selectedUser?.status === 'Completed'
                    ? 'success'
                    : selectedUser?.status === 'InProgress'
                      ? 'primary'
                      : 'default'
                }
                size="medium"
                icon={
                  selectedUser?.status === 'Completed' ? (
                    <CorrectIcon aria-hidden="true" />
                  ) : undefined
                }
              />
              {isOverdue && selectedUser?.status !== 'Completed' && (
                <Chip
                  label="Overdue"
                  color="error"
                  size="medium"
                  icon={<IncorrectIcon aria-hidden="true" />}
                />
              )}
            </Box>
          </Box>
          <Box className="mb-4">
            <Box className="flex items-center justify-between mb-1">
              <Typography
                variant="body2"
                color={progressColor}
                id="progress-label"
              >
                Progress: {Math.round(progressPercentage)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedUser?.completedCourses || 0} /{' '}
                {selectedUser?.totalCourses || 0} courses
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 8, borderRadius: 4 }}
              color={progressColor}
              aria-labelledby="progress-label"
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </Box>

          <Box className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Paper className="p-4" sx={{ bgcolor: 'background.paperDark' }}>
              <Typography
                variant="h6"
                color="success.main"
                className="flex items-center gap-2 mb-4"
                id="completed-courses-heading"
              >
                Completed Courses ({selectedUser?.completedCourses || 0})
              </Typography>
              <Box
                className="overflow-auto"
                sx={{ maxHeight: 400 }}
                role="region"
                aria-labelledby="completed-courses-heading"
                tabIndex={0}
              >
                {completedCourses.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="py-4 italic text-center"
                  >
                    No completed courses
                  </Typography>
                ) : (
                  <Box component="ul" className="p-0 m-0 list-none">
                    {completedCourses.map((courseProgress, index) => (
                      <Box
                        component="li"
                        key={courseProgress.courseID || index}
                        className="flex items-center gap-2 py-1 mb-1"
                      >
                        <CorrectIcon
                          sx={{ fontSize: 18 }}
                          color="success"
                          aria-hidden="true"
                        />
                        <Typography variant="body2">
                          {courseProgress.courseName || `Course ${index + 1}`}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: 'right',
                            flexGrow: '1',
                          }}
                        >
                          Completed{' '}
                          {dayjs
                            .utc(courseProgress.completionDate)
                            .local()
                            .format('MM/DD/YYYY')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>

            <Paper className="p-4" sx={{ bgcolor: 'background.paperDark' }}>
              <Typography
                variant="h6"
                color="warning.main"
                className="flex items-center gap-2 mb-4"
                id="outstanding-courses-heading"
              >
                Outstanding Courses (
                {(assignment?.courses?.length || 0) -
                  (selectedUser?.completedCourses || 0)}
                )
              </Typography>
              <Box
                className="overflow-auto"
                sx={{ maxHeight: 400 }}
                role="region"
                aria-labelledby="outstanding-courses-heading"
                tabIndex={0}
              >
                {outstandingCourses.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="py-4 italic text-center"
                  >
                    All courses completed!
                  </Typography>
                ) : (
                  <Box component="ul" className="p-0 m-0 list-none">
                    {outstandingCourses.map(
                      (course: CourseWithStats, index: number) => (
                        <Box
                          component="li"
                          key={course.id}
                          className="flex items-center justify-between gap-2 py-1 mb-1"
                        >
                          <Box className="flex items-center gap-2">
                            <CourseIcon
                              sx={{ fontSize: 18 }}
                              color="warning"
                              aria-hidden="true"
                            />
                            <Typography variant="body2">
                              {getCourseNameForCredit(course, index)}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CorrectIcon aria-hidden="true" />}
                            disabled={isSubmitting}
                            onClick={() => {
                              let courseName = getCourseNameForCredit(
                                course,
                                index,
                              )
                              setSelectedDataForCredit({
                                courseId: course.id,
                                courseName,
                                userId: userID!,
                                userName: selectedUser?.userName || '',
                              })
                              setCourseCreditModalOpen(true)
                            }}
                            aria-label={`Give ${selectedUser?.userName} credit for ${getCourseNameForCredit(course, index)}`}
                          >
                            Give Credit
                          </Button>
                        </Box>
                      ),
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Card>
      )}
      <BulkCourseCreditModal
        isOpen={courseCreditModalOpen}
        courseGroups={
          selectedDataForCredit
            ? new Map([
                [
                  selectedDataForCredit.courseId,
                  {
                    courseID: selectedDataForCredit.courseId,
                    courseName: selectedDataForCredit.courseName,
                    userIDs: [selectedDataForCredit.userId],
                    userNames: [selectedDataForCredit.userName],
                  },
                ],
              ])
            : new Map()
        }
        totalUsers={1}
        isSubmitting={isSubmitting}
        onClose={() => {
          setSelectedDataForCredit(null)
          setCourseCreditModalOpen(false)
        }}
        onConfirm={handleGiveCourseCredit}
      />
      {NotificationComponent}
    </Container>
  )
}
